<template>
  <div>
    <b-card v-if="needSetupPayment">
      <div class="text-danger">{{ $t('payments.pleaseSetYourPaymentDetailsOn') }}
        <router-link :to="{ name: 'profile'}">
          {{ $t('payments.profile') }}
        </router-link>
        {{ $t('payments.toReceivePayments') }}
      </div>
    </b-card>
    <div
      id="payment_page"
      class="card"
    >
      <div class="table-responsive mb-0">
        <table
          role="table"
          class="table b-table table-striped table-hover"
        >
          <thead
            role="rowgroup"
            class=""
          >
            <tr
              role="row"
              class=""
            >
              <th
                role="columnheader"
                aria-colindex="0"
                :aria-sort="arialSort('date_from')"
                @click="sortCol('date_from')"
              >
                {{ $t('payments.period') }}
              </th>
              <th
                role="columnheader"
                aria-colindex="2"
                :aria-sort="arialSort('amount')"
                @click="sortCol('amount')"
              >
                {{ $t('apps.amount') }}
              </th>
              <th
                role="columnheader"
                aria-colindex="2"
                :aria-sort="arialSort('payment_method')"
                @click="sortCol('payment_method')"
              >
                {{ $t('payments.paymentDetails') }}
              </th>
              <th
                role="columnheader"
                aria-colindex="3"
                :aria-sort="arialSort('status')"
                @click="sortCol('status')"
              >
                {{ $t('apps.status') }}
              </th>
              <th
                role="columnheader"
                aria-colindex="4"
                :aria-sort="arialSort('due_date')"
                @click="sortCol('due_date')"
              >
                {{ $t('payments.dueDate') }}
              </th>
              <th>
                {{ $t('payments.actions') }}
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            <template v-for="(rowTr, indextr) in items">
              <tr
                :key="indextr"
                :class="rowTr.class"
              >
                <td>
                  {{ rowTr.period_start | dotDateTimeShort }}
                  -
                  {{ rowTr.period_end | dotDateTimeShort }}
                </td>
                <td>
                  $ {{ rowTr.amount_text }}
                </td>
                <td>
                  {{ systemLabel(rowTr.payment_method_text) }} <br>
                  <template v-for="(value, key) in rowTr.payment_details">
                    <div :key="key">
                      <span style="text-transform:capitalize;">{{ key }}</span>: <span style="text-transform:capitalize;">{{
                        value
                      }}</span>
                    </div>
                  </template>
                </td>
                <td>
                  <b-badge
                    v-if="rowTr.status === 0"
                    variant="light-primary"
                  >
                    {{ systemLabel(rowTr.status_text) }}
                  </b-badge>
                  <b-badge
                    v-if="rowTr.status === 1"
                    variant="light-success"
                  >
                    {{ systemLabel(rowTr.status_text) }}
                  </b-badge>
                  <b-badge
                    v-if="rowTr.status === 2"
                    variant="light-secondary"
                  >
                    {{ systemLabel(rowTr.status_text) }}
                  </b-badge>
                </td>
                <td>
                  <span v-if="rowTr.due_date">{{ rowTr.due_date }}</span>
                </td>
                <td>
                  <b-button
                    v-if="$hasPermission('export_payment_data')"
                    variant="success"
                    size="sm"
                    @click="csvDownload(rowTr.id)"
                  >
                    {{ $t('payments.csvDownload') }}
                  </b-button>
                  <b-button
                    v-if="$hasPermission('download_credit_notes') && rowTr.credit_note"
                    variant="primary"
                    size="sm"
                    class="ml-1"
                    :disabled="creditNoteLoading[rowTr.id]"
                    @click="openCreditNote(rowTr.id)"
                  >
                    {{ $t('payments.creditNote') }}
                  </b-button>
                  <b-dropdown
                    v-else-if="$hasPermission('download_credit_notes') && rowTr.credit_notes && rowTr.credit_notes.length"
                    variant="primary"
                    size="sm"
                    class="ml-1"
                    right
                    boundary="window"
                    :disabled="creditNoteLoading[rowTr.id]"
                    :text="`${$t('payments.creditNotes')} (${rowTr.credit_notes.length})`"
                  >
                    <b-dropdown-item
                      v-for="creditNote in rowTr.credit_notes"
                      :key="creditNote.id"
                      @click="openPaymentCreditNote(rowTr.id, creditNote.id)"
                    >
                      {{ creditNote.note_number }} · {{ creditNote.description }}
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <b-card-footer>
        <b-row>
          <b-col
            v-show="items.length > 0"
            cols="12"
          >
            <pagination-and-table-info
              :data="paginationData"
              @change="handleChangePage($event)"
            />
          </b-col>
          <b-col
            v-show="items.length === 0"
            cols="12"
            style="font-weight: 500; text-align: center"
          >
            {{ $t('apps.noData') }}
          </b-col>
        </b-row>
      </b-card-footer>
    </div>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'
import { systemLabel } from '@/libs/i18n/display'

import PaginationAndTableInfo from '@ps_main/custom-components/PaginationAndTableInfo.vue'

export default {
  components: {
    PaginationAndTableInfo,
  },
  data() {
    return {
      url: '/api/payments',
      items: [],
      params: {
        page: 1,
        order_by: 'id',
        item_per_page: 5,
        order_direction: 'desc',
      },
      responseData: null,
      needSetupPayment: false,
      creditNoteLoading: {},
    }
  },
  computed: {
    paginationData() {
      return this.laravelPaginationPages(this.responseData)
    },
  },
  created() {
    window.Payments = this
    this.search()
  },
  mounted() {
    this.$http.get('/api/profile-settings/get').then(response => {
      this.needSetupPayment = response.data.profile.payment_details === null
    }).catch(errors => {
      this.alertApiError(errors, i18n.t('payments.failedToLoadProfileSettings'))
    })
  },
  methods: {
    systemLabel,
    handleChangePage(page) {
      this.params.page = page
      this.search()
    },
    sortCol(fieldName) {
      if (this.params.order_by === fieldName) {
        this.params.order_direction = this.params.order_direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.params.order_by = fieldName
        this.params.order_direction = 'asc'
      }
      this.search()
    },
    arialSort(sortFieldName) {
      if (this.params.order_by === sortFieldName) {
        return this.params.order_direction === 'asc'
          ? 'ascending'
          : 'descending'
      }
      return 'none'
    },
    search() {
      this.showLoading()
      this.$http
        .get(`${this.url}/get`, {
          params: {
            ...this.params,
          },
        })
        .then(response => {
          this.items = response.data.data
          this.responseData = response.data
          this.isBusy = false
        })
        .catch(errors => {
          this.items = []
          this.responseData = null
          this.alertApiError(errors, i18n.t('payments.paymentsRequestFailed'))
          this.isBusy = false
        })
        .finally(() => {
          this.hideLoading()
        })
    },
    csvDownload(paymentId) {
      const exportLocale = i18n.locale
      this.isBusy = true
      this.showLoading()
      this.$http
        .get(`${this.url}/export/${paymentId}`, {
          params: {
            ...this.params,
            ...this.filterValues,
            locale: exportLocale,
            only_file_name: true,
          },
        })
        .then(response => {
          const filename = response.data

          return this.$http
            .get(`${this.url}/export/${paymentId}`, {
              params: {
                ...this.params,
                ...this.filterValues,
                locale: exportLocale,
              },
              responseType: 'blob',
            })
            .then(responseExport => {
              const url = window.URL.createObjectURL(new Blob([responseExport.data]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', filename)
              document.body.appendChild(link)
              link.click()
            })
            .catch(errors => {
              this.alertApiError(errors, i18n.t('payments.paymentExportFailed'))
            })
        })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('payments.paymentExportFailed'))
        })
        .finally(() => {
          this.hideLoading()
          this.isBusy = false
        })
    },
    openCreditNote(paymentId) {
      this.$set(this.creditNoteLoading, paymentId, true)
      this.$http
        .get(`${this.url}/credit-note/${paymentId}`, {
        })
        .then(response => {
          if (response.data.url) {
            window.open(response.data.url, '_blank')
          }
        })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('payments.creditNoteRequestFailed'))
        })
        .finally(() => {
          this.$set(this.creditNoteLoading, paymentId, false)
        })
    },
    openPaymentCreditNote(paymentId, creditNoteId) {
      // Open the tab while still inside the click: browsers block window.open after an awaited request
      const creditNoteTab = window.open('', '_blank')
      if (creditNoteTab) {
        creditNoteTab.opener = null
      }

      this.$set(this.creditNoteLoading, paymentId, true)
      this.$http
        .get(`${this.url}/${paymentId}/credit-notes/${creditNoteId}`)
        .then(response => {
          if (!response.data.url) {
            if (creditNoteTab) {
              creditNoteTab.close()
            }

            return
          }

          if (creditNoteTab) {
            creditNoteTab.location.href = response.data.url
          } else {
            window.open(response.data.url, '_blank')
          }
        })
        .catch(errors => {
          if (creditNoteTab) {
            creditNoteTab.close()
          }
          this.alertApiError(errors, i18n.t('payments.creditNoteRequestFailed'))
        })
        .finally(() => {
          this.$set(this.creditNoteLoading, paymentId, false)
        })
    },
  },
}

</script>

<style>
</style>
